// The two production grids and the orders grid.
//
// Their column orders are the source's, read during the phase 6 sign-off. Two
// of the three needed more than a reordering:
//
//   `Two Week Production View` shows a job number, a customer name and a
//   product, none of which are columns on `two_week_production`. They come from
//   the order -- see `orderIndex.ts`, and D15 for why the product must.
//
//   `Daily Production Tracker Table` calls its first column `Order Number`,
//   which is what the source calls it there. The same value is `Job Number` on
//   every barrier screen, which is what the source calls it there. Both names
//   are the source's, for one column.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type GridColumn } from '../grid/DataGrid';
import { Screen } from '../shell/Screen';
import { useCollection } from '../data';
import { useOrderIndex, withOrderFacts, type OrderIndex } from './orderIndex';
import * as api from '../api';
import type { Row } from '../types';

function orderOptions(index: OrderIndex | null): { value: number; label: string }[] | undefined {
  if (!index) return undefined;
  return [...index.entries()]
    .map(([value, facts]) => ({ value, label: facts.order_number ?? String(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** `Two Week Production View` -- grouped by week, with the only numeric
 *  aggregate in the application on the group headers.
 *
 *  `week_commencing` is a date rather than the source's free-text horizon: the
 *  source held ten distinct values in two formats and every one was a Monday,
 *  so a date groups and sorts with no machinery. The group header sums
 *  `Outlook Build` over it. */
export function TwoWeekProductionScreen() {
  const rows = useCollection('two-week-production');
  const index = useOrderIndex();
  const navigate = useNavigate();

  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);

  const columns = useMemo<GridColumn<Row>[]>(
    () => [
      {
        key: 'order_number',
        label: 'Job Number',
        type: 'str',
        width: 130,
        editable: true,
        editKey: 'order_id',
        editFrom: (row) => row.order_id,
        options: orderOptions(index.data),
      },
      { key: 'order_customer_name', label: 'Customer Name', type: 'str', width: 190 },
      // `Product Type` is what the source calls it here, and `Product` is what
      // it calls the reference on the barrier grids. Two names for two columns
      // that hold the same kind of value, both of them the source's.
      { key: 'order_product_type', label: 'Product Type', type: 'str', width: 150, className: 'style-product' },
      // No `value` on a typed column: the grid formats by type, and formatting
      // here as well rendered a date as a midnight timestamp.
      { key: 'week_commencing', label: 'Week', type: 'date', width: 130, editable: true },
      { key: 'plan_build', label: 'Plan Build', type: 'int', width: 110, align: 'right', editable: true },
      { key: 'plan_ship', label: 'Plan Ship', type: 'int', width: 110, align: 'right', editable: true },
      {
        key: 'outlook_build',
        label: 'Outlook Build',
        type: 'int',
        width: 130,
        align: 'right',
        editable: true,
        aggregate: 'sum',
      },
      { key: 'outlook_ship', label: 'Outlook Ship', type: 'int', width: 120, align: 'right', editable: true },
      { key: 'comments', label: 'Comments', type: 'str', width: 320, editable: true },
    ],
    [index.data]
  );

  return (
    <Screen
      title="Two Week Production View"
      description="Grouped by week, with SUM(Outlook Build) on each group."
      loading={rows.loading}
      error={rows.error ?? index.error}
      count={resolved.length}
      actions={
        <button className="primary" onClick={() => navigate('/data/two-week-production/new')}>
          New row
        </button>
      }
    >
      <DataGrid
        rows={resolved}
        columns={columns}
        getRowId={(row) => String(row.two_week_production_id)}
        groupBy={['week_commencing']}
        onEdit={async (row, key, value) => {
          await api.updateRow('two-week-production', row.two_week_production_id as number, { [key]: value });
          rows.reload();
        }}
        onRowClick={(row) => navigate(`/data/two-week-production/${row.two_week_production_id}`)}
        emptyMessage="No two-week production rows."
      />
    </Screen>
  );
}

/** `Daily Production Tracker Table` -- grouped by order number, most recent
 *  first, and the one screen carrying conditional formatting that reads the row.
 *
 *  Four columns against `Daily Target`: below is red, at or above is green and
 *  bold. The source writes that as eight rules; it is one rule over four
 *  columns here. A row with no target is neither, because "below nothing" is
 *  not a judgement the data supports. */
export function DailyProductionScreen() {
  const rows = useCollection('daily-production');
  const index = useOrderIndex();
  const navigate = useNavigate();

  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);
  const columns = useMemo(() => dailyProductionColumns(index.data), [index.data]);

  return (
    <Screen
      title="Daily Production Tracker Table"
      description="Grouped by order number, latest date first."
      loading={rows.loading}
      error={rows.error ?? index.error}
      count={resolved.length}
      actions={
        <button className="primary" onClick={() => navigate('/data/daily-production/new')}>
          New row
        </button>
      }
    >
      <DataGrid
        rows={resolved}
        columns={columns}
        getRowId={(row) => String(row.daily_production_id)}
        groupBy={['order_number']}
        sortBy={[{ key: 'production_date', desc: true }]}
        onEdit={async (row, key, value) => {
          await api.updateRow('daily-production', row.daily_production_id as number, { [key]: value });
          rows.reload();
        }}
        onRowClick={(row) => navigate(`/data/daily-production/${row.daily_production_id}`)}
        emptyMessage="No daily production rows."
      />
    </Screen>
  );
}

/** Exported because both the screen and the dashboard pane draw the same table. */
export function dailyProductionColumns(index: OrderIndex | null): GridColumn<Row>[] {
  const performance = (key: string) => (row: Row) => {
    const target = row.daily_target;
    const value = row[key];
    if (typeof target !== 'number' || typeof value !== 'number') return undefined;
    return value < target ? 'perf-under' : 'perf-met';
  };

  return [
    {
      key: 'order_number',
      // The source calls it `Order Number` on this view and `Job Number` on the
      // barrier views. Both labels are its own.
      label: 'Order Number',
      type: 'str',
      width: 140,
      editable: true,
      editKey: 'order_id',
      editFrom: (row) => row.order_id,
      options: orderOptions(index),
    },
    { key: 'production_date', label: 'Date', type: 'date', width: 120, editable: true },
    { key: 'daily_target', label: 'Daily Target', type: 'int', width: 110, align: 'right', editable: true },
    { key: 'assembly', label: 'Assembly', type: 'int', width: 100, align: 'right', editable: true, cellClass: performance('assembly') },
    { key: 'wiring', label: 'Wiring', type: 'int', width: 100, align: 'right', editable: true, cellClass: performance('wiring') },
    { key: 'test', label: 'Test', type: 'int', width: 100, align: 'right', editable: true, cellClass: performance('test') },
    { key: 'shipped', label: 'Shipped', type: 'int', width: 100, align: 'right', editable: true, cellClass: performance('shipped') },
    { key: 'comments', label: 'Comments', type: 'str', width: 360, editable: true },
  ];
}

/** `Orders View`.
 *
 *  The source draws this as a card deck with swipe actions. D10 closed that as
 *  a table: 43 orders render in the same grid component as every other screen.
 *  The deck's fields are its primary header `Job Name`, its secondary header
 *  `Order Number`, and the product it groups by -- so those are the columns,
 *  under the target's names for them.
 *
 *  `Job Name` is the customer's name rather than a name for the job; phase 3
 *  established that and the load carried it to `customer_name`.
 *
 *  Over the open orders, which is the slice the source's deck is built on --
 *  so `Status` is `Open` on every row and is not a column here. */
export function OrdersScreen() {
  const rows = useCollection('orders', { status: 'Open' });
  const index = useOrderIndex();
  const navigate = useNavigate();

  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);
  const columns = useMemo(() => ordersColumns(), []);

  return (
    <Screen
      title="Orders View"
      description="Open orders, grouped by product."
      loading={rows.loading}
      error={rows.error ?? index.error}
      count={resolved.length}
      actions={
        <button className="primary" onClick={() => navigate('/data/orders/new')}>
          New order
        </button>
      }
    >
      <DataGrid
        rows={resolved}
        columns={columns}
        getRowId={(row) => String(row.order_id)}
        groupBy={['order_product_type']}
        sortBy={[{ key: 'order_number' }]}
        onEdit={async (row, key, value) => {
          await api.updateRow('orders', row.order_id as number, { [key]: value });
          rows.reload();
        }}
        onRowClick={(row) => navigate(`/data/orders/${row.order_id}`)}
        emptyMessage="No open orders."
      />
    </Screen>
  );
}

export function ordersColumns(): GridColumn<Row>[] {
  return [
    { key: 'order_number', label: 'Order Number', type: 'str', width: 140, editable: true },
    { key: 'customer_name', label: 'Customer Name', type: 'str', width: 220, editable: true },
    { key: 'order_product_type', label: 'Product', type: 'str', width: 160, className: 'style-product' },
  ];
}
